"use client";

import { useState, useEffect } from "react";
import { Camera, Image as ImageIcon, MapPin } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useAppSelector } from "@/hooks/hooks";
import Image from "next/image";
import dynamic from "next/dynamic";

const ImageGalleryModal = dynamic(() => import("./ImageGalleryModal"), { ssr: false });

interface GalleryImage {
  ID: number;
  ResortID: number;
  Image: string;
}

interface ResortGallery {
  ID: number;
  Title: string;
  GalleryImages: GalleryImage[];
}

interface GalleryClientProps {
  galleryData: ResortGallery[];
  cmsData?: any;
}

export default function GalleryClient({
  galleryData,
  cmsData,
}: GalleryClientProps) {
  const [activeResortId, setActiveResortId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const BannerList = useAppSelector((state) => state.home.BannerList);
  const banner: any =
    (BannerList || []).find((b: any) => b?.BannerType === "gallery") || {};

  // Filter out resorts that don't have any images
  const resortsWithImages = (galleryData || []).filter(
    (resort) => resort.GalleryImages && resort.GalleryImages.length > 0,
  );

  // Set first resort as active by default
  useEffect(() => {
    if (activeResortId === null && resortsWithImages.length > 0) {
      setActiveResortId(resortsWithImages[0].ID);
    }
  }, [resortsWithImages, activeResortId]);

  const activeResort = resortsWithImages.find((r) => r.ID === activeResortId);
  const activeImages =
    activeResort?.GalleryImages?.map((img) => img.Image) || [];

  const openImageModal = (index: number) => {
    setPhotoIndex(index);
    setIsModalOpen(true);
  };

  return (
    <main className="min-h-screen bg-gray-50 pb-20 select-none">
      {/* Hero Section */}
      <section className="relative h-[45vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          {banner?.BannerImage ? (
            <>
              <Image
                src={banner.BannerImage}
                alt={banner.AltTitle || banner.BannerTitle || "Our Gallery"}
                fill
                priority
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/80"></div>
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-pink-900 via-purple-900 to-indigo-950"></div>
          )}
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl">
          <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-yellow-300 font-semibold text-sm uppercase tracking-wider mb-4">
            Visual Tour
          </span>

          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 drop-shadow-lg font-sans">
            {banner?.BannerTitle || "Our Gallery"}
          </h1>

          <p className="text-lg md:text-xl text-gray-200 font-sans font-light drop-shadow-md leading-relaxed">
            {banner?.Description ||
              "Take a visual tour through the magic, luxury, and celebrations of Rann Utsav's premium tent cities."}
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-10">
        {resortsWithImages.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl shadow-sm border border-gray-100">
            <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-600">
              No Gallery Images Available
            </h3>
            <p className="text-gray-400 mt-2">Please check back later.</p>
          </div>
        ) : (
          <>
            {/* Resort Tabs/Selector */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {resortsWithImages.map((resort) => {
                const isActive = activeResortId === resort.ID;
                return (
                  <button
                    key={resort.ID}
                    onClick={() => setActiveResortId(resort.ID)}
                    className={`px-6 py-3.5 rounded-xl font-medium font-sans text-sm md:text-base shadow-sm border transition-all duration-300 cursor-pointer flex items-center gap-2 ${
                      isActive
                        ? "bg-gradient-to-r from-pink-500 via-pink-600 to-purple-600 text-white border-transparent hover:shadow-md"
                        : "bg-white text-gray-700 border-gray-200/80 hover:bg-gray-50 hover:text-pink-600"
                    }`}
                  >
                    <MapPin
                      className={`w-4 h-4 ${isActive ? "text-yellow-300" : "text-gray-400"}`}
                    />
                    <span>{resort.Title}</span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        isActive
                          ? "bg-white/20 text-white"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {resort.GalleryImages.length}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Selected Resort Gallery */}
            {activeResort && (
              <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-gray-100/80">
                {/* <div className="flex flex-col md:flex-row md:items-center justify-between pb-6 mb-8 border-b border-gray-100">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-sans font-semibold text-gray-900 leading-tight">
                      {activeResort.Title}
                    </h2>
                    <p className="text-gray-500 mt-1">
                      Showing {activeResort.GalleryImages.length} photos of the
                      resort and surroundings
                    </p>
                  </div>
                </div> */}

                {/* Images Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {activeResort.GalleryImages.map((img, idx) => (
                    <div
                      key={img.ID}
                      onClick={() => openImageModal(idx)}
                      className="group relative h-64 sm:h-72 rounded-2xl overflow-hidden cursor-pointer shadow-sm hover:shadow-lg border border-gray-100 bg-gray-50"
                    >
                      <ImageWithFallback
                        src={img.Image}
                        alt={`${activeResort.Title} Gallery Image ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />

                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <div className="p-3 bg-white/20 backdrop-blur-md rounded-full text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                          <ImageIcon className="w-6 h-6" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Lightbox Modal */}
            <ImageGalleryModal
              images={activeImages}
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              initialIndex={photoIndex}
            />
          </>
        )}
      </div>
    </main>
  );
}
